/**
 * Product index name from Theme settings (`algolia_product_index` → `window.algoliaPlp.productIndexName`)
 * @returns {string}
 */
export function getAlgoliaProductIndexName() {
  return window.algoliaPlp.productIndexName;
}

/**
 * Algolia record `price` / `compare_at_price` units.
 * - `false`: major units (e.g. 80 for $80.00) — multiply by 100 before `formatMoney` / card.js.
 * - `true`: cents like Liquid `variant.price` (e.g. 8000) — pass through.
 * Must match how your index stores `price` for numeric filters to work (see `ALGOLIA_PRICE_FILTER_SCALE`).
 */
export var ALGOLIA_PRICE_RECORD_IN_CENTS = false;

/** Price range inputs are in dollars; scale converts to the same units as the index. */
export var ALGOLIA_PRICE_FILTER_SCALE = ALGOLIA_PRICE_RECORD_IN_CENTS ? 100 : 1;

/**
 * Convert Algolia hit price fields to Shopify cents for `formatMoney` and product card JSON.
 * @param {unknown} value
 * @returns {number}
 */
export function algoliaPriceToShopifyCents(value) {
  var n = Number(value);
  if (Number.isNaN(n)) return 0;
  return ALGOLIA_PRICE_RECORD_IN_CENTS ? Math.round(n) : Math.round(n * 100);
}

/** Numeric Algolia attribute for the price range filter. */
export var ALGOLIA_PRICE_ATTRIBUTE = 'price';

/** Facet attribute for colour (variant option). */
export var ALGOLIA_COLOR_ATTRIBUTE = 'options.color';

/** Facet attribute and toggle value for the Sale Only checkbox (rendered inside the Price group). */
export var ALGOLIA_SALE_ATTRIBUTE = 'meta.content.is_on_sale';
export var ALGOLIA_SALE_TOGGLE_VALUE = 'true';

/**
 * PLP facet definitions (order matches design). Each `attribute` must exist on
 * records and be in `attributesForFaceting` for facet counts.
 *
 * @type {Array<{ attribute: string, label: string, type: 'refinementList' | 'rangeInput' | 'colorSwatches', limit?: number }>}
 */
export var ALGOLIA_PLP_FACETS = [{
  attribute: 'price',
  label: 'Price',
  type: 'rangeInput'
}, {
  attribute: ALGOLIA_COLOR_ATTRIBUTE,
  label: 'Colour',
  type: 'colorSwatches'
}, {
  attribute: 'meta.content.capacity',
  label: 'Capacity',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.laptop_size',
  label: 'Laptop Size',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.use_case',
  label: 'Use Case',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.gender_age',
  label: 'Gender/Age',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.category',
  label: 'Category',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.size',
  label: 'Size',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.hat_type',
  label: 'Hat Type',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.collection',
  label: 'Collection',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.material',
  label: 'Material',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.organization',
  label: 'Organization',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.travel_features',
  label: 'Travel Features',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.closure_type',
  label: 'Closure Type',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.luggage_size',
  label: 'Luggage Size',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.luggage_type',
  label: 'Luggage Type',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.weather_protection',
  label: 'Weather Protection',
  type: 'refinementList'
}, {
  attribute: 'meta.filters.vendor',
  label: 'Vendor',
  type: 'refinementList'
}];

/**
 * Sort options for PLP: Featured (main product index) plus theme-configured replicas.
 * @returns {Array<{ label: string, value: string }>}
 */
export function getAlgoliaSortReplicas() {
  var _window$algoliaPlp$so, _window$algoliaPlp;
  var featured = {
    label: 'Featured',
    value: getAlgoliaProductIndexName()
  };
  var extras = (_window$algoliaPlp$so = (_window$algoliaPlp = window.algoliaPlp) === null || _window$algoliaPlp === void 0 ? void 0 : _window$algoliaPlp.sortIndexPairs) !== null && _window$algoliaPlp$so !== void 0 ? _window$algoliaPlp$so : [];
  return [featured, ...extras];
}